import axios from 'axios';

// Read API URL from runtime config (window._env_) injected by docker-entrypoint.sh.
// Falls back to process.env for local `npm start` development without Docker.
//
// Runtime config is written to /env-config.js at container startup from the
// REACT_APP_API_URL environment variable set in docker-compose.yml or ECS task definition.
//
// This means you can change the API URL by restarting the container —
// no image rebuild required.
const getApiUrl = () => {
  // Runtime injection (Docker / ECS)
  if (window._env_ && window._env_.REACT_APP_API_URL) {
    return window._env_.REACT_APP_API_URL;
  }
  // Local development with `npm start` (no Docker)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // eslint-disable-next-line no-console
  console.error(
    '[Payment Terms Engine] API URL is not configured.\n' +
    'Set REACT_APP_API_URL as a runtime environment variable.\n' +
    'Docker:  add it under "environment:" in docker-compose.yml\n' +
    'AWS ECS: add it to the task definition environment section'
  );
  return '';
};

const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000,
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');
export const register = (data) => api.post('/auth/register', data);

// Purchase Orders
export const getPOs = (params) => api.get('/pos', { params });
export const getPO = (id) => api.get(`/pos/${id}`);
export const getPOEditability = (id) => api.get(`/pos/${id}/editability`);
export const getPORevisions = (id) => api.get(`/pos/${id}/revisions`);
export const startSoModification = (data) => api.post('/so-modifications', data);
export const getSoModification = (id) => api.get(`/so-modifications/${id}`);
export const resolveSoModificationGst = (id) => api.post(`/so-modifications/${id}/resolve-gst`);
export const listSoModificationsForPo = (poId) => api.get(`/so-modifications/po/${poId}`);
export const previewSoModification = (id, data) => api.post(`/so-modifications/${id}/impact-preview`, data);
export const submitSoModification = (id) => api.post(`/so-modifications/${id}/submit`);
export const approveSoModification = (id) => api.post(`/so-modifications/${id}/approve`);
export const cancelSoModification = (id) => api.post(`/so-modifications/${id}/cancel`);
export const rejectSoModification = (id, data) => api.post(`/so-modifications/${id}/reject`, data);
export const executeSoModification = (id) => api.post(`/so-modifications/${id}/execute`);
export const getReversalQueue = (params) => api.get('/reversal-queue', { params });
export const createPO = (data) => api.post('/pos', data);
export const updatePO = (id, data) => api.put(`/pos/${id}`, data);
export const deletePO = (id) => api.delete(`/pos/${id}`);
export const syncSoBridge = (id) => api.post(`/pos/${id}/bridge-sync`);
export const confirmPO = (id) => api.post(`/pos/${id}/confirm`);
export const acceptDelivery = (id, data) => api.post(`/pos/${id}/delivery-acceptance`, data);
export const getPaymentSchedule = (poId) => api.get(`/pos/${poId}/payment-schedule`);

// Payment Terms
export const getPaymentTerms = (params) => api.get('/payment-terms', { params });
export const getPaymentTerm = (id) => api.get(`/payment-terms/${id}`);
export const createPaymentTerm = (data) => api.post('/payment-terms', data);
export const updatePaymentTerm = (id, data) => api.put(`/payment-terms/${id}`, data);
export const deletePaymentTerm = (id) => api.delete(`/payment-terms/${id}`);
export const getPaymentTermEditability = (id) => api.get(`/payment-terms/${id}/editability`);
export const amendPaymentTerm = (id, data) => api.put(`/payment-terms/${id}/amend`, data);
export const bindPaymentTerm = (termId, poId) => api.post(`/payment-terms/${termId}/bind/${poId}`);
export const getTemplates = (params) => api.get('/payment-terms/templates', { params });

// Installments
export const getInstallment = (id) => api.get(`/installments/${id}`);
export const triggerInstallment = (id) => api.post(`/installments/${id}/trigger`);
export const recordInstallmentPayment = (id, data) => api.post(`/installments/${id}/record-payment`, data);
export const waiveInstallment = (id, data) => api.post(`/installments/${id}/waive`, data);

// Milestones
export const submitMilestone = (id) => api.post(`/milestones/${id}/submit`);
export const acceptMilestone = (id) => api.post(`/milestones/${id}/accept`);
export const rejectMilestone = (id, data) => api.post(`/milestones/${id}/reject`, data);

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const getPOInvoices = (poId) => api.get(`/invoices/pos/${poId}/invoices`);
export const approveInvoice = (id) => api.post(`/invoices/${id}/approve`);
export const rejectInvoice = (id, data) => api.post(`/invoices/${id}/reject`, data);
export const sendInvoice = (id) => api.post(`/invoices/${id}/send`);
export const cancelInvoice = (id, data) => api.post(`/invoices/${id}/cancel`, data);
export const reverseInvoiceToO2c = (id, data) => api.post(`/invoices/${id}/reverse-o2c`, data);
export const recordInvoicePayment = (id, data) => api.post(`/invoices/${id}/record-payment`, data);
export const disputeInvoice = (id, data) => api.post(`/invoices/${id}/dispute`, data);
export const getInvoiceAuditLog = (id) => api.get(`/invoices/${id}/audit-log`);

// Revenue
export const getRevenueSchedules = (params) => api.get('/revenue-schedules', { params });
export const getRevenueSchedule = (id) => api.get(`/revenue-schedules/${id}`);
export const getRevenueReport = (period) => api.get('/revenue-recognition/report', { params: { period } });
export const getDeferredBalance = () => api.get('/revenue-recognition/deferred-balance');

// Unbilled Revenue
export const getUnbilledRevenue  = (params) => api.get('/unbilled-revenue', { params });
export const getUnbilledAgeing   = () => api.get('/unbilled-revenue/ageing');
export const getUnbilledSummary  = () => api.get('/unbilled-revenue/summary');
export const getUnbilledReport = (period) => api.get('/unbilled-revenue/report', { params: { period } });

// Subscriptions / Auto Renew
export const getSubscriptionPolicies = () => api.get('/subscriptions');
export const runRenewalEvaluation = () => api.post('/subscriptions/evaluate');
export const getSubscription = (id) => api.get(`/subscriptions/${id}`);
export const renewSubscription = (id) => api.post(`/subscriptions/${id}/renew`);
export const approveRenewal = (id) => api.post(`/subscriptions/${id}/approve-renewal`);
export const rejectRenewal = (id, data) => api.post(`/subscriptions/${id}/reject-renewal`, data);
export const cancelSubscription = (id, data) => api.post(`/subscriptions/${id}/cancel`, data);
export const upsertSubscriptionConfig = (termId, data) => api.post(`/payment-terms/${termId}/subscription-config`, data);

// Dashboard & misc
export const getDashboardSummary = () => api.get('/dashboard/summary');
export const getNotifications = (params) => api.get('/notifications', { params });
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const getAuditLog = (params) => api.get('/audit-log', { params });

export default api;

// Organisations
export const getOrganisations = (params) => api.get('/organisations', { params });
export const getOrganisation = (id) => api.get(`/organisations/${id}`);
export const createOrganisation = (data) => api.post('/organisations', data);
export const updateOrganisation = (id, data) => api.put(`/organisations/${id}`, data);

// Users
export const getUsers = (params) => api.get('/users', { params });
export const getUser = (id) => api.get(`/users/${id}`);
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const toggleUserActive = (id) => api.patch(`/users/${id}/toggle-active`);
export const resetUserPassword = (id, data) => api.patch(`/users/${id}/reset-password`, data);

// Customers (Buyers)
export const getCustomers = (params) => api.get('/customers', { params });
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const toggleCustomerActive = (id) => api.patch(`/customers/${id}/toggle-active`);

// Force-trigger an invoice for any installment regardless of trigger condition
export const forceInvoice = (id, data) => api.post(`/installments/${id}/force-trigger`, data);

// Revenue schedules (create via invoice)
export const createRevenueSchedule = (data) => api.post('/revenue-schedules', data);
export const previewEqualMonthly = (data) => api.post('/revenue-schedules/preview-equal-monthly', data);
export const getRevenueSchedulesByInvoice = (invoiceId) => api.get('/revenue-schedules', { params: { invoice_id: invoiceId } });
export const postRevenueEntry = (scheduleId, data) => api.post(`/revenue-schedules/${scheduleId}/post-period`, data);
export const reverseRevenueSchedule = (scheduleId, data) => api.post(`/revenue-schedules/${scheduleId}/reverse`, data);

// Unbilled Revenue — Scenario 2 (Ind AS 115)
export const createUnbilledEntry   = (data) => api.post('/unbilled-revenue', data);
export const raiseInvoiceFromUnbilled = (id, data) => api.post(`/unbilled-revenue/${id}/raise-invoice`, data);
export const getUnbilledEntry      = (id) => api.get(`/unbilled-revenue/${id}`);
export const getJournalEntries     = (params) => api.get('/unbilled-revenue/journal-entries', { params });
export const reverseUnbilledRevenue = (id, data) => api.post(`/unbilled-revenue/${id}/reverse`, data);
export const getPOBalance = (poId) => api.get(`/pos/${poId}/balance`);

// PO lifecycle
export const approvePO = (poId) => api.post(`/pos/${poId}/approve`);
export const getPOTriggers = (poId) => api.get(`/pos/${poId}/triggers`);
export const actionTrigger = (installmentId, data) => api.post(`/milestones/triggers/${installmentId}/action`, data);

// Admin: reset all test/transaction data
export const resetTestData = () => api.post('/admin/reset-test-data', { confirm: 'DELETE_ALL_TEST_DATA' });
export const deduplicateSeedData = () => api.post('/admin/reset-seed-data', { confirm: 'DEDUPLICATE_SEED_DATA' });

// Document Attachments
export const uploadAttachment  = (data) => api.post('/attachments', data);
export const getAttachments    = (entityType, entityId) => api.get('/attachments', { params: { entity_type: entityType, entity_id: entityId } });
export const downloadAttachment= (id) => api.get(`/attachments/${id}/download`);
export const deleteAttachment  = (id) => api.delete(`/attachments/${id}`);
export const approveJournalEntry = (id) => api.post(`/admin/approve-journal/${id}`);
export const runNightlyBatch = () => api.post('/admin/run-nightly-batch');
export const processDueInstallments = (poId) => api.post(`/pos/${poId}/process-due-installments`);
export const reprocessSubscriptionRevRec = (invoiceId) => api.post(`/invoices/${invoiceId}/reprocess-subscription-revrec`);

// Platform Admin
export const getPlatformStats    = () => api.get('/platform-admin/stats');
export const getPlatformOrgs     = () => api.get('/platform-admin/orgs');
export const createPlatformOrg   = (data) => api.post('/platform-admin/orgs', data);
export const updatePlatformOrg   = (id, data) => api.patch(`/platform-admin/orgs/${id}`, data);
export const getOrgUsers         = (orgId) => api.get(`/platform-admin/orgs/${orgId}/users`);
export const createOrgUser       = (orgId, data) => api.post(`/platform-admin/orgs/${orgId}/users`, data);
export const toggleOrgUser       = (userId) => api.patch(`/platform-admin/users/${userId}/toggle`);
